import React from "react";
import { ChatMessage } from "./types";
import { colorFromId, initials, noSaveProps, formatBubbleTime } from "./helpers";
import { CheckIcon, KebabIcon, LocationPinIcon, PaperclipIcon, ExclamationIcon } from "./icons";
import { t } from "../../utils/i18n";

function renderMessageContent(msg: ChatMessage, isMe: boolean, lang: string) {
  if (msg.type === "image" && msg.media_url) {
    return <img src={msg.media_url} alt="" {...noSaveProps} onClick={() => window.open(msg.media_url, "_blank")} style={{ ...noSaveProps.style, maxWidth: 220, maxHeight: 280, borderRadius: 14, cursor: "pointer", display: "block" }} />;
  }
  if (msg.type === "audio" && msg.media_url) {
    return <audio controls controlsList="nodownload" src={msg.media_url} {...noSaveProps} style={{ ...noSaveProps.style, maxWidth: 240, height: 36 }} />;
  }
  if (msg.type === "location") {
    let coords: { lat: number; lng: number } | null = null;
    try { coords = JSON.parse(msg.content || "{}"); } catch { coords = null; }
    const bubbleStyle: React.CSSProperties = { backgroundColor: isMe ? "white" : "#0a0a0a", color: isMe ? "#0a0a0a" : "white", borderRadius: 18, padding: "9px 14px", fontSize: 13, fontWeight: 600, border: isMe ? "1px solid #F0F0F0" : "none", textDecoration: "none", display: "inline-block" };
    const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6 };
    if (!coords) return <div style={bubbleStyle}><span style={rowStyle}><LocationPinIcon size={14} /> {t(lang,"msg_location_shared")||"Position partagée"}</span></div>;
    return <a href={`https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}#map=16/${coords.lat}/${coords.lng}`} target="_blank" rel="noreferrer" style={bubbleStyle}><span style={rowStyle}><LocationPinIcon size={14} /> {t(lang,"msg_location_view_map")||"Position partagée, voir sur la carte"}</span></a>;
  }
  if (msg.type === "file" && msg.media_url) {
    const bubbleStyle: React.CSSProperties = { backgroundColor: isMe ? "white" : "#0a0a0a", color: isMe ? "#0a0a0a" : "white", borderRadius: 18, padding: "9px 14px", fontSize: 13, fontWeight: 600, border: isMe ? "1px solid #F0F0F0" : "none", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 };
    return <a href={msg.media_url} target="_blank" rel="noreferrer" onContextMenu={e => e.preventDefault()} style={bubbleStyle}><PaperclipIcon size={14} /> {msg.content || (t(lang,"msg_file")||"Fichier")}</a>;
  }
  return (
    <div style={{ backgroundColor: isMe ? "white" : "#0a0a0a", color: isMe ? "#0a0a0a" : "white", borderRadius: 18, borderBottomRightRadius: isMe ? 4 : 18, borderBottomLeftRadius: isMe ? 18 : 4, padding: "9px 14px", fontSize: 14, lineHeight: 1.5, border: isMe ? "1px solid #F0F0F0" : "none", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      {msg.content}
    </div>
  );
}

// Bulle de message unique. Le regroupement des messages consécutifs (façon Messenger) est
// calculé en amont dans Messages.tsx (isGroupStart/isGroupEnd, sur toute la salve) : ici on ne
// fait qu'appliquer les conséquences visuelles — avatar/nom seulement en tête de salve, heure et
// coches seulement en fin de salve par défaut (un tap sur n'importe quelle bulle de la salve les
// révèle ponctuellement pour CETTE bulle précise, via `revealed`).
export function MessageBubble({
  lang, msg, isMe, isGroup, showAvatar, isGroupEnd, isRead, revealed, onToggleReveal,
  menuOpen, onToggleMenu, onCloseMenu, onEdit, onDelete, onRetry,
}: {
  lang: string;
  msg: ChatMessage;
  isMe: boolean;
  isGroup: boolean; // discussion de groupe (community/ritual) : affiche avatar/nom des autres
  showAvatar: boolean;
  isGroupEnd: boolean;
  isRead: boolean;
  revealed: boolean;
  onToggleReveal: () => void;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRetry: () => void;
}) {
  const status = msg.status || "sent";
  const showMeta = isGroupEnd || revealed;
  const isFailed = status === "failed";

  let tick: React.ReactNode = null;
  if (isMe) {
    if (isFailed) tick = <ExclamationIcon />;
    else if (status === "sending") tick = <CheckIcon />;
    else if (isRead) tick = <CheckIcon double read />;
    else tick = <CheckIcon read />;
  }

  return (
    <div
      style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: isGroupEnd ? 10 : 3, alignItems: "flex-end", gap: 8 }}
    >
      {!isMe && isGroup && (
        <div style={{ width: 28, flexShrink: 0 }}>
          {showAvatar && <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: colorFromId(msg.user_id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 9, fontWeight: 700 }}>{initials(msg.user_name || "")}</div>}
        </div>
      )}
      <div style={{ maxWidth: "60%", display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
        {showAvatar && !isMe && isGroup && <span style={{ fontSize: 11, color: "#999", fontWeight: 600, marginBottom: 3, marginLeft: 4 }}>{msg.user_name}</span>}
        <div
          onClick={() => (isFailed ? onRetry() : onToggleReveal())}
          style={{ cursor: isFailed ? "pointer" : "default", opacity: status === "sending" ? 0.65 : 1 }}
          title={isFailed ? (t(lang,"msg_send_failed")||"Échec de l'envoi, toucher pour réessayer") : undefined}
        >
          {renderMessageContent(msg, isMe, lang)}
        </div>
        {showMeta && (
          <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: isFailed ? "#c0392b" : "#bbb", marginTop: 3, marginLeft: 4, marginRight: 4 }}>
            {msg.is_edited && `${t(lang,"msg_edited_label")||"Modifié"} · `}
            {isFailed ? (
              <span onClick={onRetry} style={{ cursor: "pointer", fontWeight: 600 }}>{t(lang,"msg_send_failed")||"Échec, toucher pour réessayer"}</span>
            ) : (
              formatBubbleTime(msg.created_at)
            )}
            {tick}
            {isMe && status !== "sending" && status !== "failed" && (
              <span style={{ position: "relative", display: "inline-flex" }}>
                <button onClick={onToggleMenu} style={{ border: "none", background: "none", cursor: "pointer", color: "#bbb", padding: 0, display: "flex", marginLeft: 2 }}>
                  <KebabIcon />
                </button>
                {menuOpen && (
                  <>
                    <div onClick={onCloseMenu} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
                    <div style={{ position: "absolute", bottom: "120%", right: 0, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.14)", zIndex: 30, minWidth: 130 }}>
                      {msg.type === "text" && (
                        <button onClick={onEdit} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#0a0a0a" }}>{t(lang,"msg_edit_action")||"Modifier"}</button>
                      )}
                      <button onClick={onDelete} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#c0392b", borderTop: msg.type === "text" ? "1px solid #F5F5F5" : "none" }}>{t(lang,"msg_delete_action")||"Supprimer"}</button>
                    </div>
                  </>
                )}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
